import type { AnalyticsTrends } from '@/lib/analytics'

function formatMoney(value: number): string {
  return `${value.toLocaleString('ru-RU', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} ₽`
}

function trendChange(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null) return null
  if (previous === 0) return current === 0 ? 0 : null
  return (current - previous) / previous
}

function TrendBadge({ value }: { value: number | null }) {
  if (value === null) return null
  const rounded = Math.round(value * 10) / 10
  const positive = rounded > 0
  const negative = rounded < 0
  return (
    <span className={`shrink-0 text-[12px] font-bold leading-none tabular-nums sm:text-[13px] ${positive ? 'text-[#4E653C]' : negative ? 'text-[#944438]' : 'text-ink-muted'}`}>
      {positive ? '↑' : negative ? '↓' : '→'} {Math.abs(rounded).toLocaleString('ru-RU')}%
    </span>
  )
}

function MetricCard({
  label,
  value,
  note,
  trend,
  unavailable = false,
  preserveNote = false,
}: {
  label: string
  value: string
  note: string
  trend: number | null
  unavailable?: boolean
  preserveNote?: boolean
}) {
  const displayedNote = unavailable || preserveNote || trend !== null ? note : 'нет полного предыдущего периода'
  return (
    <article className="surface-card flex min-h-[104px] flex-col justify-between p-3 sm:min-h-[128px] sm:p-5">
      <div className="eyebrow">{label}</div>
      <div>
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <div className="text-[21px] font-semibold leading-none tracking-tight text-ink tab-nums sm:text-[28px]">{value}</div>
          {trend !== null && <span className="inline-flex items-center text-[12px] font-bold leading-none text-ink-muted sm:text-[13px]">(<TrendBadge value={trend} />)</span>}
        </div>
        <div className="mt-1.5 text-[9px] leading-snug text-ink-muted sm:mt-2 sm:text-[11px]">{displayedNote}</div>
      </div>
    </article>
  )
}

export function CustomerKpiCards({ trends }: { trends: AnalyticsTrends }) {
  const currentReturningRate = trends.repeatRate.current
  const currentReturningRateValid = currentReturningRate !== null
    && Number.isFinite(currentReturningRate)
    && currentReturningRate >= 0
    && currentReturningRate <= 100
  const previousReturningRate = trends.repeatRate.previous
  const previousReturningRateValid = previousReturningRate !== null
    && Number.isFinite(previousReturningRate)
    && previousReturningRate >= 0
    && previousReturningRate <= 100
  const currentCustomers = trends.uniqueCustomers.current
  const currentReturningCountValid = trends.repeatRate.currentReturningCustomers >= 0
    && trends.repeatRate.currentReturningCustomers <= currentCustomers
  const expectedReturningRate = currentCustomers > 0 && currentReturningCountValid
    ? trends.repeatRate.currentReturningCustomers / currentCustomers * 100
    : null
  const previousCustomers = trends.uniqueCustomers.previous
  const previousReturningCountValid = trends.repeatRate.previousReturningCustomers >= 0
    && trends.repeatRate.previousReturningCustomers <= previousCustomers
  const expectedPreviousRate = previousCustomers > 0 && previousReturningCountValid
    ? trends.repeatRate.previousReturningCustomers / previousCustomers * 100
    : null
  const returningAvailable = trends.repeatRate.availability === 'available'
    && currentReturningRateValid
    && expectedReturningRate !== null
    && Math.abs(currentReturningRate - expectedReturningRate) < 0.000001
  const previousReturningAvailable = trends.repeatRate.previousAvailability === 'available'
    && previousReturningRateValid
    && expectedPreviousRate !== null
    && Math.abs(previousReturningRate - expectedPreviousRate) < 0.000001
  const returningTrend = previousReturningAvailable
    ? trendChange(currentReturningRate, previousReturningRate)
    : null
  const identifiedOrders = trends.repeatRate.currentIdentifiedOrders
  const qualifyingOrders = trends.repeatRate.currentQualifyingOrders
  const coverage = Number.isFinite(identifiedOrders) && Number.isFinite(qualifyingOrders)
    ? ` · покупатель известен в ${identifiedOrders.toLocaleString('ru-RU')} из ${qualifyingOrders.toLocaleString('ru-RU')} заказов`
    : ''
  const mature = trends.matureRepeatRate
  const matureCurrent = mature?.current
  const matureAvailable = mature?.availability === 'available' && matureCurrent !== null && matureCurrent !== undefined
  const averageAvailable = trends.averageCheck.availability === 'available' && trends.averageCheck.current !== null

  return (
    <div className="mb-3 grid grid-cols-1 gap-2 sm:grid-cols-2 sm:gap-3 xl:grid-cols-4">
      <MetricCard
        label="Средний чек · 30 дней"
        value={averageAvailable && trends.averageCheck.current !== null ? formatMoney(trends.averageCheck.current) : '—'}
        note={averageAvailable ? 'изменение к предыдущим 30 дням' : 'Недоступно: в периоде нет заказов для расчёта среднего чека.'}
        trend={trends.averageCheck.changeAvailability === 'available' && trends.averageCheck.changePercent !== null ? trends.averageCheck.changePercent * 100 : null}
        unavailable={!averageAvailable}
        preserveNote={!averageAvailable}
      />
      <MetricCard
        label="Покупатели · 30 дней"
        value={trends.uniqueCustomers.current.toLocaleString('ru-RU')}
        note="изменение к предыдущим 30 дням"
        trend={trends.uniqueCustomers.changeAvailability === 'available' && trends.uniqueCustomers.changePercent !== null ? trends.uniqueCustomers.changePercent * 100 : null}
      />
      <MetricCard
        label="Вернулись в активные 30 дней"
        value={returningAvailable && currentReturningRate !== null ? `${currentReturningRate.toFixed(1)}%` : '—'}
        note={returningAvailable ? `${trends.repeatRate.currentReturningCustomers.toLocaleString('ru-RU')} покупателей уже были с нами раньше${coverage}` : 'Недоступно: нет согласованной когорты покупателей для расчёта.'}
        trend={returningAvailable && trends.repeatRate.changeAvailability === 'available' && returningTrend !== null ? returningTrend * 100 : null}
        unavailable={!returningAvailable}
        preserveNote={!returningAvailable}
      />
      <MetricCard
        label="Повторили за первые 30 дней"
        value={matureAvailable ? `${matureCurrent.toFixed(1)}%` : '—'}
        note={matureAvailable
          ? `${mature.currentRepeatedCustomers.toLocaleString('ru-RU')} из ${mature.currentMatureCustomers.toLocaleString('ru-RU')} клиентов с первым заказом 30–60 дней назад повторили в первые 30 дней`
          : 'Недоступно: нет зрелой когорты клиентов с первым заказом 30–60 дней назад.'}
        trend={matureAvailable && mature.changeAvailability === 'available' && mature.changePercent !== null ? mature.changePercent * 100 : null}
        unavailable={!matureAvailable}
        preserveNote
      />
    </div>
  )
}
